import React, { useEffect, useMemo, useState } from "react";

import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";

import { concat, omit } from "lodash";

import FilterDropdown, {
  FilterDropDownButton
} from "components/filter-dropdown";

import FilterAutocomplete from "./filter-autocomplete.component";
import Filter from "./filter.component";

export type CustomFilterOption = {
  label: string;
  filterKey: string;
  renderViewMode?: (filterKey: string, metadata: any) => React.ReactNode;
  editComponent?: (
    filterKey: string,
    setMetadata: (metadata: any) => void
  ) => React.ReactNode;
  onEditTriggered?: (filterKey: string) => void;
  onApplyEdit?: (filterKey: string) => void;
  onCancelEdit?: (filterKey: string) => void;
  onDelete?: (filterKey: string) => void;
};

type CustomFilterContainerProps = {
  tooltipText?: string;
  moreFiltersLabel?: string;
  filters: CustomFilterOption[];
  mostCommonFilters?: CustomFilterOption[];
  selectedFilters?: Record<string, any>;
  wrap?: boolean;
};

export default function CustomFiltersList({
  tooltipText = "Click to add a custom filter",
  moreFiltersLabel = "More Filters",
  filters,
  mostCommonFilters,
  selectedFilters,
  wrap
}: CustomFilterContainerProps) {
  const isSmallScreen = useMediaQuery("(max-width:1512px)");
  const [optionInEditMode, setOptionInEditMode] = useState<
    string | null | undefined
  >();
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>(
    []
  );
  const [selectedFiltersMeta, setSelectedFiltersMeta] = useState<
    Record<string, any>
  >({});

  useEffect(() => {
    const selected = Object.keys(selectedFilters || {}).filter(
      (key) => (selectedFilters || {})[key] !== null
    );
    // Deduplicate to prevent exponential array growth on re-renders
    setSelectedOptionValues((prev) => {
      const merged = new Set([...prev, ...selected]);
      return Array.from(merged);
    });
    setSelectedFiltersMeta((prev) => ({ ...prev, ...selectedFilters }));
  }, [selectedFilters]);

  const [selectedOptions, availableOptions] = useMemo(() => {
    return [
      filters.filter((option) =>
        selectedOptionValues.includes(option.filterKey)
      ),
      filters
        .filter((option) => !selectedOptionValues.includes(option.filterKey))
        .map(({ label, filterKey }) => ({
          label,
          filterKey
        }))
    ];
  }, [selectedOptionValues, filters]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleIconButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (
    option: { label: string; filterKey: string } | null
  ) => {
    setSelectedOptionValues(
      concat(selectedOptionValues, String(option?.filterKey))
    );
    setOptionInEditMode(option?.filterKey);
  };

  const handleClearMetadata = (value: string) => {
    setSelectedFiltersMeta(omit(selectedFiltersMeta, value));
    setSelectedOptionValues([
      ...selectedOptionValues.filter((optionValue) => optionValue !== value)
    ]);
  };

  const handleFinishEdit = (value: string) => {
    setOptionInEditMode("");
  };

  const handleSetFilterData = (value: string, data: any) => {
    if (data === null || data === undefined) {
      handleClearMetadata(value);
    } else {
      setSelectedFiltersMeta({
        ...selectedFiltersMeta,
        [value]: data
      });
    }
    handleFinishEdit(value);
  };

  const handleStartEdit = (value: string) => {
    setOptionInEditMode(value);
  };

  return (
    <Stack
      direction={isSmallScreen ? "column" : "row"}
      rowGap={1}
      columnGap={2}
      data-testid="filters-bar"
      alignItems={isSmallScreen ? "flex-start" : "center"}
      justifyContent="flex-start"
      sx={{
        mr: 1,
        mt: wrap ? 3 : undefined,
        flexWrap: wrap ? "wrap" : undefined
      }}
    >
      {mostCommonFilters?.map((filter) => (
        <FilterDropdown
          key={filter.filterKey}
          option={filter}
          isEdit={optionInEditMode === filter.filterKey}
          handleSetFilterData={handleSetFilterData}
          selectedFiltersMeta={selectedFiltersMeta}
          metadata={selectedFiltersMeta[filter.filterKey]}
          handleClearMetadata={handleClearMetadata}
          onStartEdit={handleStartEdit}
          onFinishEdit={handleFinishEdit}
        />
      ))}
      {Boolean(selectedOptionValues.length) && (
        <Stack spacing={2} direction="row" alignItems="center">
          {selectedOptions.map((option) => (
            <Filter
              key={option.filterKey}
              option={option}
              metadata={selectedFiltersMeta[option.filterKey]}
              isEdit={optionInEditMode === option.filterKey}
              onStartEdit={handleStartEdit}
              onFinishEdit={handleFinishEdit}
              handleClearMetadata={handleClearMetadata}
              handleSetFilterData={handleSetFilterData}
            />
          ))}
        </Stack>
      )}
      {Boolean(availableOptions.length) && (
        <Tooltip title={tooltipText}>
          <span>
            <FilterDropDownButton
              label={moreFiltersLabel}
              handleClick={handleIconButtonClick}
            />{" "}
          </span>
        </Tooltip>
      )}
      <FilterAutocomplete
        options={availableOptions}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </Stack>
  );
}
